import { useState, useEffect } from 'react';

type Theme = 'dark' | 'light' | 'system';

export function useTheme() {
  const [mounted, setMounted] = useState(false);
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('theme') as Theme) || 'dark';
    }
    return 'dark';
  });

  const [systemTheme, setSystemTheme] = useState<'dark' | 'light'>('light');

  // Only run effect on client-side to avoid hydration mismatch
  useEffect(() => {
    setMounted(true);

    // Set initial system theme
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemTheme(mediaQuery.matches ? 'dark' : 'light');

    // Handle system theme changes
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme to document
  useEffect(() => {
    if (!mounted) return;

    const root = window.document.documentElement;
    const activeTheme = theme === 'system' ? systemTheme : theme;

    root.classList.remove('dark', 'light');
    root.classList.add(activeTheme);

    localStorage.setItem('theme', theme);
  }, [theme, systemTheme, mounted]);

  const currentTheme = theme === 'system' ? systemTheme : theme;

  return {
    theme,
    systemTheme,
    currentTheme,
    toggleTheme: () => {
      setTheme((current) => {
        if (current === 'dark') return 'light';
        if (current === 'light') return 'system';
        return 'dark';
      });
    },
    setTheme: (newTheme: Theme) => setTheme(newTheme),
    // Return mounted to help with handling SSR/hydration
    mounted,
  };
}
